import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { MfaRequiredError, completeMfaLogin, getCurrentUser, isEmailVerified, requiresProfileCompletion, signInWithCustomToken } from '../../services/auth/authService';
import { getFirebaseAuth } from '../../services/firebaseConfig';
import {
  initRecaptcha,
  isMfaEnrolled,
  startMfaSignIn,
  completeMfaSignIn
} from '../../services/auth/mfaService';
import {
  preAuthCheck,
  sessionLogin,
  isPlatformAuthenticatorSupported,
  lookupWebAuthnCredentials,
  loginWithWebAuthn,
} from '../../services/auth/securitySessionService';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { Card } from '../atoms/Card';
import { normalizeEmail } from '../../utils/inputSecurity';
import './LoginPage.css';

const LAST_LOGIN_EMAIL_KEY = 'macha.lastLoginEmail';

/**
 * Module summary:
 * This page handles the full sign-in experience. It supports password login,
 * Google login, passkey login, trusted-device pre-checks, and the second-step
 * MFA challenge when Firebase requires it.
 */
export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState(() => localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || '');
  const [password, setPassword] = useState('');
  const { user, isAuthenticated, login, loginWithGoogle, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [hasPasskeyForIdentifier, setHasPasskeyForIdentifier] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [preAuthMessage, setPreAuthMessage] = useState('');

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaResolver, setMfaResolver] = useState<any>(null);
  const [mfaPendingUser, setMfaPendingUser] = useState<any>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const logoStyle: React.CSSProperties = {
    width: 78,
    height: 'auto',
    margin: '0 auto 0.7rem',
    objectFit: 'contain',
  };
  // Tracks whether the SMS code has already been sent for the current MFA session.
  const mfaSmsSentRef = useRef(false);

  const from = (location.state as any)?.from?.pathname || '/home';
  // Fall back to the dashboard when no protected route originally triggered the login redirect.

  const finalizeServerSession = async (sourceLabel: string) => {
    const fbUser = getFirebaseAuth().currentUser;
    if (!fbUser) return;
    try {
      // Exchange the Firebase client session for a backend session cookie so
      // server-side routes can trust later requests from this browser.
      const idToken = await fbUser.getIdToken();
      await sessionLogin({
        idToken,
        rememberDevice,
        label: sourceLabel,
      });
    } catch (err) {
      console.warn('Server session bootstrap failed, keeping Firebase client session only:', err);
    }
  };

  // When the MFA view is shown, the login-form DOM is replaced, so the
  // RecaptchaVerifier must be (re-)initialized against the new element that is
  // now in the DOM.  We do this in an effect (runs after paint) to guarantee
  // the new element exists before calling Firebase.
  useEffect(() => {
    if (mfaStep && mfaResolver && !mfaSmsSentRef.current) {
      mfaSmsSentRef.current = true;
      initRecaptcha('login-recaptcha-container');
      setMfaLoading(true);
      startMfaSignIn(mfaResolver)
        .then(vid => setMfaVerificationId(vid))
        .catch(err => setMfaError(err instanceof Error ? err.message : 'Failed to send verification code'))
        .finally(() => setMfaLoading(false));
    }
    // Reset the sent-flag when the user goes back to the login form so that
    // a subsequent MFA challenge will send a fresh SMS code.
    if (!mfaStep) {
      mfaSmsSentRef.current = false;
    }
  }, [mfaStep, mfaResolver]);

  useEffect(() => {
    let cancelled = false;
    const checkPasskeySupport = async () => {
      // Ask the browser once whether this device supports a built-in passkey authenticator.
      const supported = await isPlatformAuthenticatorSupported();
      if (!cancelled) setPasskeySupported(supported);
    };
    void checkPasskeySupport();
    return () => {
      cancelled = true;
    };
  }, []);

  const rememberLastLoginEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    localStorage.setItem(LAST_LOGIN_EMAIL_KEY, trimmed);
  };

  useEffect(() => {
    if (!passkeySupported) {
      setHasPasskeyForIdentifier(false);
      return;
    }
    const identifier = email.trim();
    if (!identifier) {
      setHasPasskeyForIdentifier(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      // Delay the lookup slightly so the app does not call the backend on every keystroke immediately.
      void lookupWebAuthnCredentials(identifier)
        .then((hasCredentials) => {
          if (!cancelled) setHasPasskeyForIdentifier(hasCredentials);
        })
        .catch(() => {
          if (!cancelled) setHasPasskeyForIdentifier(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [email, passkeySupported]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedEmail = normalizeEmail(email);

    try {
      setPreAuthMessage('');
      if (normalizedEmail) {
        // Ask the backend for trusted-device risk hints so the page can set user expectations before login.
        const pre = await preAuthCheck(normalizedEmail);
        if (pre.trusted && !pre.requireMfa) {
          setPreAuthMessage('Trusted device recognized. You may not need another MFA challenge.');
        } else if (pre.requireRecaptcha) {
          setPreAuthMessage('Additional anti-abuse checks may be required for this sign-in.');
        }
      }
      await login(normalizedEmail, password);
      rememberLastLoginEmail(normalizedEmail);
      await finalizeServerSession('email-password');
      const signedInUser = await getCurrentUser();
      const nextPath = requiresProfileCompletion(signedInUser) ? '/profile' : from;
      // Successful login without MFA challenge  -  user either has no MFA
      // enrolled or Firebase did not require a second factor.
      // If MFA is not enrolled, redirect to MFA setup for first-time enrollment.
      // If MFA is enrolled, go directly to the destination (returning user).
      if (!isMfaEnrolled()) {
        navigate(isEmailVerified() ? '/mfa-setup' : '/verify-email', { replace: true });
        return;
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        // Returning user with MFA enrolled  -  Firebase requires second factor.
        // Show the MFA verification screen; the useEffect will reinitialize
        // reCAPTCHA on the newly-rendered element and send the SMS code.
        setMfaResolver(err.resolver);
        setMfaPendingUser(err.pendingUser);
        setMfaError('');
        setMfaCode('');
        setMfaStep(true);
      }
      // Other errors handled by useAuth hook
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode.trim()) {
      setMfaError('Please enter the verification code.');
      return;
    }
    setMfaError('');
    setMfaLoading(true);
    try {
      await completeMfaSignIn(mfaResolver, mfaVerificationId, mfaCode.trim());
      // For mock mode, complete the login with the pending user
      if (mfaPendingUser) {
        completeMfaLogin(mfaPendingUser);
      }
      rememberLastLoginEmail(email);
      await finalizeServerSession('mfa');
      const signedInUser = await getCurrentUser();
      navigate(requiresProfileCompletion(signedInUser) ? '/profile' : from, { replace: true });
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaCode('');
    setMfaVerificationId('');
    setMfaResolver(null);
    setMfaPendingUser(null);
    setMfaError('');
  };

  const handleGoogleSignIn = async () => {
    try {
      await loginWithGoogle();
      rememberLastLoginEmail(email);
      await finalizeServerSession('google-oauth');
      const signedInUser = await getCurrentUser();
      const nextPath = requiresProfileCompletion(signedInUser) ? '/profile' : from;
      // If MFA is not enrolled, redirect to MFA setup for first-time enrollment.
      if (!isMfaEnrolled()) {
        navigate(isEmailVerified() ? '/mfa-setup' : '/verify-email', { replace: true });
        return;
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        // Returning user with MFA enrolled  -  show verification screen.
        // The useEffect will reinitialize reCAPTCHA and send the SMS code.
        setMfaResolver(err.resolver);
        setMfaPendingUser(err.pendingUser);
        setMfaError('');
        setMfaCode('');
        setMfaStep(true);
      }
      // Other errors handled by useAuth hook
    }
  };

  const handlePasskeySignIn = async () => {
    setPasskeyMessage('');
    setPasskeyLoading(true);
    try {
      const normalizedEmail = normalizeEmail(email);
      if (!normalizedEmail) {
        throw new Error('Enter your email address to use Face ID sign-in.');
      }
      const result = await loginWithWebAuthn(normalizedEmail, rememberDevice);
      // Sign in with the custom token returned by the backend after WebAuthn verification succeeds.
      await signInWithCustomToken(result.customToken);
      rememberLastLoginEmail(normalizedEmail);
      await finalizeServerSession('webauthn-passkey');
      const signedInUser = await getCurrentUser();
      navigate(requiresProfileCompletion(signedInUser) ? '/profile' : from, { replace: true });
      return;
    } catch (err) {
      setPasskeyMessage(err instanceof Error ? err.message : 'Passkey sign-in failed.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  if (loading && !mfaStep) {
    return (
      <div className="login-page">
        <Card className="login-page__card" padding="lg" shadow="lg">
          <div className="login-page__header">
            <img src="/Transparent-Logo.png" alt="Macha Group" style={logoStyle} />
            <h1 className="login-page__title">Checking Authentication</h1>
            <p className="login-page__subtitle">Restoring your session...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to={isMfaEnrolled() ? from : (isEmailVerified() ? '/mfa-setup' : '/verify-email')} replace />;
  }

  // MFA Verification Screen
  if (mfaStep) {
    return (
      <div className="login-page">
        <Card className="login-page__card" padding="lg" shadow="lg">
          <div className="login-page__header">
            <img src="/Transparent-Logo.png" alt="Macha Group" style={logoStyle} />
            <h1 className="login-page__title">Verification Required</h1>
            <p className="login-page__subtitle">
              A verification code has been sent to your phone via SMS.
            </p>
          </div>

          {/* Recaptcha container (invisible) */}
          <div id="login-recaptcha-container" ref={recaptchaRef} />

          <form onSubmit={handleMfaVerify} className="login-page__form">
            {mfaError && (
              <div className="login-page__error">
                {mfaError}
              </div>
            )}

            <Input
              type="text"
              label="Verification Code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder="Enter 6-digit code"
              required
              fullWidth
            />
            <label className="login-page__security-option">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              Remember this browser for trusted-device checks
            </label>

            <p className="login-page__hint">
              Staying signed in prevents repeat MFA on refresh. After an explicit sign-out, Firebase may still require MFA unless you use a passkey or console-level trusted-device support is enabled.
            </p>

            <Button
              type="submit"
              fullWidth
              loading={mfaLoading}
              size="lg"
            >
              Verify &amp; Sign In
            </Button>

            <Button
              type="button"
              fullWidth
              size="lg"
              variant="secondary"
              onClick={handleBackToLogin}
              disabled={mfaLoading}
            >
              Back to Login
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="login-page">
      <Card className="login-page__card" padding="lg" shadow="lg">
        <div className="login-page__header">
          <img src="/Transparent-Logo.png" alt="Macha Group" style={logoStyle} />
          <h1 className="login-page__title">Macha Group Security</h1>
          <p className="login-page__subtitle">Sign in to access assessments</p>
        </div>

        <form onSubmit={handleSubmit} className="login-page__form">
          {error && (
            <div className="login-page__error">
              {error}
            </div>
          )}

          <Input
            type="email"
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            fullWidth
          />

          <Input
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            fullWidth
          />
          <label className="login-page__security-option">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Remember this browser for trusted-device checks
          </label>

          {preAuthMessage && (
            <p className="login-page__hint">{preAuthMessage}</p>
          )}

          <Button 
            type="submit" 
            fullWidth 
            loading={loading}
            size="lg"
          >
            Sign In
          </Button>

          <div className="login-page__divider">
            <hr />
            <span>or</span>
            <hr />
          </div>

          <div className="login-page__security-stack">
            <Button
              type="button"
              fullWidth
              size="lg"
              variant="secondary"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Sign in with Google
              </span>
            </Button>

            {passkeySupported && hasPasskeyForIdentifier && (
              <Button
                type="button"
                fullWidth
                size="lg"
                variant="secondary"
                onClick={handlePasskeySignIn}
                loading={passkeyLoading}
                disabled={loading || passkeyLoading}
              >
                Sign in with Face ID / Biometrics
              </Button>
            )}
          </div>

          {passkeySupported && email.trim() && !hasPasskeyForIdentifier && (
            <p className="login-page__hint">No passkey is registered for this email yet. Use your password or Google sign-in.</p>
          )}

          {passkeyMessage && <p className="login-page__hint">{passkeyMessage}</p>}
        </form>

        {/* Recaptcha container (invisible)  -  always in DOM for MFA readiness */}
        <div id="login-recaptcha-container" ref={recaptchaRef} />

        <div className="login-page__footer">
          <p className="auth-links" style={{ marginTop: '0.7rem' }}>
            <Link to="/create-account">Create account</Link>  |  <Link to="/forgot-password">Forgot password</Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;


